import { Visibility, VisibilityOff } from "@mui/icons-material";
import {
  Box,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import useAPI from "../../../../api/hooks/useAPI";
import useColors from "../../../../api/hooks/useColors";
import useIsIntegrated from "../../../../api/hooks/useIsIntegrated";
import { integrationKeys } from "../../../../api/queryKeys";
import ContainedButton from "../../../../components/buttons/ContainedButton";
import LoadingIcon from "../../../../components/LoadingIcon";
import { ScreenshotAccordion } from "../../../../components/ScreenshotAccordion";
import { Toast } from "../../../../components/Toast";
import { isUrlValid } from "../../../../utils/utils";
import { logos } from "../../environment/icons/iconExport";
import Guideline from "../components/Guideline";
import GuidelineStep from "../components/GuidelineStep";
import GuidelineStepByStep from "../components/GuidelineStepByStep";
import add_new_token_button from "./images/add_new_token_button.png";
import navigate_to_group from "./images/navigate_to_group.png";
import navigate_settings_access_tokens from "./images/settings_access tokens.png";
import token_details from "./images/token_details.png";
const textboxStyle = {
  "&& .MuiOutlinedInput-notchedOutline": {
    borderColor: "rgba(0,0,0,0.23) !important",
    borderWidth: "1px !important",
    transition: "none",
  },
};

const GitLabIntegration = () => {
  const { registerGitLabIntegration } = useAPI();
  const { isIntegrated, isIntegratedLoading, isIntegratedSuccess } =
    useIsIntegrated("gitlab");
  const colors = useColors();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const [groupPageURL, setGroupPageURL] = useState("");
  const [groupToken, setGroupToken] = useState("");
  const [showGroupToken, setShowGroupToken] = useState(false);

  const guideMode = searchParams.has("viewonly");
  const isTokenValid =
    groupToken.startsWith("glpat-") && groupToken.length > 30;

  const registerGitLabMutation = useMutation({
    mutationFn: () => registerGitLabIntegration(groupPageURL, groupToken),
    onError: () => {
      Toast.error("Error connecting to GitLab");
    },
    onSuccess: () => {
      Toast.success("GitLab connected successfully");
      queryClient.invalidateQueries({
        queryKey: integrationKeys.all,
      });
    },
  });

  return (
    <Box sx={{ width: "100%" }}>
      <Guideline
        name={"GitLab"}
        image={logos.GitLabLogo}
        title={""}
        content={
          isIntegratedLoading ? (
            <LoadingIcon size={25} />
          ) : isIntegrated && !guideMode ? (
            <Box>
              <Typography variant={"h4"} color={colors.text_primary}>
                GitLab is registered!
              </Typography>
            </Box>
          ) : isIntegratedSuccess ? (
            <Box>
              <GuidelineStepByStep>
                <GuidelineStep title="Copy your group page URL">
                  <Box display="flex" flexDirection="column" gap={2}>
                    <Typography variant="body1" color={colors.text_primary}>
                      Login to GitLab using a Group Owner account and navigate
                      to the group you will integrate.
                    </Typography>
                    <ScreenshotAccordion
                      images={[
                        {
                          file: navigate_to_group,
                          altext: "navigate to group",
                        },
                      ]}
                      onExpanded={() => {}}
                    />
                    <Typography variant="body1" color={colors.text_primary}>
                      Please copy the group page URL and paste below. Page URL
                      example:
                    </Typography>
                    <code
                      style={{
                        fontSize: "12px",
                        backgroundColor: colors.grey_200,
                        padding: "4px 8px",
                        borderRadius: "4px",
                      }}
                    >
                      https://gitlab.com/groupName
                    </code>
                    <Box display="flex" flexDirection="column" gap={0.5}>
                      <Typography
                        variant="subtitle2"
                        color={colors.text_primary}
                      >
                        Group page URL
                      </Typography>
                      <TextField
                        placeholder="https://"
                        fullWidth
                        type="text"
                        value={groupPageURL}
                        onChange={(event) =>
                          setGroupPageURL(event.target.value)
                        }
                        disabled={guideMode}
                        sx={textboxStyle}
                      />
                    </Box>
                  </Box>
                </GuidelineStep>
                <GuidelineStep title="Create a group access token">
                  <Box display="flex" flexDirection="column" gap={2}>
                    <Typography
                      variant="body1"
                      color={colors.text_primary}
                      sx={{ display: "flex", gap: 0.5 }}
                    >
                      In the left side menu, navigate to
                      <Typography fontWeight={500} color={colors.text_primary}>
                        Settings → Access tokens
                      </Typography>
                      .
                    </Typography>
                    <ScreenshotAccordion
                      images={[
                        {
                          file: navigate_settings_access_tokens,
                          altext: "navigate to Settings and Access tokens",
                        },
                      ]}
                      onExpanded={() => {}}
                    />
                    <Box display="flex" gap={0.5}>
                      <Typography
                        variant="body1"
                        color={colors.text_primary}
                        sx={{ display: "flex", gap: 0.5 }}
                      >
                        Click on the
                        <Typography
                          fontWeight={500}
                          color={colors.text_primary}
                        >
                          Add new token
                        </Typography>
                        button.
                      </Typography>
                    </Box>
                    <ScreenshotAccordion
                      images={[
                        {
                          file: add_new_token_button,
                          altext: "add new token button",
                        },
                      ]}
                      onExpanded={() => {}}
                    />

                    <Typography
                      variant="body1"
                      color={colors.text_primary}
                      sx={{ display: "flex", gap: 0.5 }}
                    >
                      Fill a name and description for the token, for example:
                      <code
                        style={{
                          fontSize: "12px",
                          backgroundColor: colors.grey_200,
                          padding: "4px 8px",
                          borderRadius: "4px",
                        }}
                      >
                        Breeze Security Platform
                      </code>
                      .
                    </Typography>
                    <Typography variant="body1" color={colors.text_primary}>
                      Choose the maximum expiration date (1 year).
                    </Typography>
                    <Typography
                      variant="body1"
                      color={colors.text_primary}
                      sx={{ display: "flex", gap: 0.5 }}
                    >
                      Select the
                      <Typography fontWeight={500} color={colors.text_primary}>
                        Owner
                      </Typography>
                      role and the
                      <Typography fontWeight={500} color={colors.text_primary}>
                        read_api
                      </Typography>
                      scope.
                    </Typography>
                    <ScreenshotAccordion
                      images={[
                        {
                          file: token_details,
                          altext: "token details",
                        },
                      ]}
                      onExpanded={() => {}}
                    />
                    <Typography
                      variant="body1"
                      color={colors.text_primary}
                      sx={{ display: "flex", gap: 0.5 }}
                    >
                      Click on the
                      <Typography fontWeight={500} color={colors.text_primary}>
                        Create group access token
                      </Typography>
                      button.
                    </Typography>
                    <Typography variant="body1" color={colors.text_primary}>
                      Paste below the new generated token.
                    </Typography>
                    <Box display="flex" flexDirection="column" gap={0.5}>
                      <Typography
                        variant="subtitle2"
                        color={colors.text_primary}
                      >
                        Group access token
                      </Typography>
                      <TextField
                        placeholder="glpat-XXXXXXXXXXXXXXXXXXXX"
                        fullWidth
                        type={showGroupToken ? "text" : "password"}
                        value={groupToken}
                        onChange={(event) => setGroupToken(event.target.value)}
                        disabled={guideMode}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                aria-label="toggle secret access key visibility"
                                onClick={() =>
                                  setShowGroupToken((prev) => !prev)
                                }
                              >
                                {showGroupToken ? (
                                  <Visibility />
                                ) : (
                                  <VisibilityOff />
                                )}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                        sx={textboxStyle}
                      />
                    </Box>
                    <ContainedButton
                      color="primary"
                      size="medium"
                      onClick={() => {
                        registerGitLabMutation.mutate();
                      }}
                      disabled={!isUrlValid(groupPageURL) || !isTokenValid}
                      loading={registerGitLabMutation.isPending}
                    >
                      Connect
                    </ContainedButton>
                  </Box>
                </GuidelineStep>
              </GuidelineStepByStep>
            </Box>
          ) : (
            <Box>
              <Typography variant="subtitle2" color={colors.text_primary}>
                Error loading this page. Please try again later.
              </Typography>
            </Box>
          )
        }
      />
    </Box>
  );
};

export default GitLabIntegration;
